import { createAction, createSlice } from "@reduxjs/toolkit";

const initialState = {
  progress: {
    section: null,
    assessment: null,
    chapter: null,
  },
  irLevelMapping: {
    diagnosticAssessmentUrl: null,
    levelAssignment: null,
  },
  paused: false,
  synonyms: [],
  synonymsLoading: false,
  recapCompleted: false,
  scores: [],
  audioPlaying: "",
  failed: false,
  failedWords: [],
  successWords: [],
  activeWords: [],
  activeSentence: null,
  // Store failedWords and successWords per sentence index for navigation
  failedWordsHistory: {},
  successWordsHistory: {},
  missedWordsHistory: {},
};

export const studySlice = createSlice({
  name: "study",
  initialState,
  reducers: {
    setProgress(state, action) {
      state.progress = { ...state.progress, ...action.payload };
    },

    setIRLevelMapping(state, action) {
      state.irLevelMapping = action.payload;
    },
    setPaused(state, action) {
      state.paused = action.payload;
    },
    setSynonyms(state, action) {
      state.synonyms = action.payload;
    },
    setSynonymsLoading(state, action) {
      state.synonymsLoading = action.payload;
    },
    setRecapCompleted(state, action) {
      state.recapCompleted = action.payload;
    },
    updateScores(state, action) {
      const { index, score, text } = action.payload;
      if (index === undefined || score === undefined || !text) {
        return;
      }
      const existingScore = state.scores.find((s) => s.index === index);
      if (existingScore) {
        existingScore.score = score;
      } else {
        state.scores.push({ index, score, text });
      }
    },
    clearScores(state) {
      state.scores = [];
    },
    setAudioPlaying(state, action) {
      state.audioPlaying = action.payload;
    },
    setFailed(state, action) {
      state.failed = action.payload;
    },
    setFailedWords(state, action) {
      state.failedWords = action.payload;
    },
    setSuccessWords(state, action) {
      state.successWords = action.payload;
    },
    setActiveWords(state, action) {
      state.activeWords = action.payload;
    },
    setActiveSentence(state, action) {
      state.activeSentence = action.payload;
    },
    setFailedWordsForSentence(state, action) {
      const { index, words } = action.payload;
      if (index !== undefined) {
        state.failedWordsHistory[index] = words || [];
      }
    },
    setSuccessWordsForSentence(state, action) {
      const { index, words } = action.payload;
      if (index !== undefined) {
        state.successWordsHistory[index] = words || [];
      }
    },
    setMissedWordsForSentence(state, action) {
      const { index, words } = action.payload;
      if (index !== undefined) {
        state.missedWordsHistory[index] = words || [];
      }
    },
    clearWordsHistory(state) {
      state.failedWordsHistory = {};
      state.successWordsHistory = {};
      state.missedWordsHistory = {};
      state.failedWords = [];
      state.successWords = [];
      state.activeWords = [];
      state.failed = false;
      state.activeSentence = null;
    },
  },
});

export const studyActions = {
  assignCourse: createAction(`${studySlice.name}/assignCourse`, (payload) => ({
    payload,
  })),
  setProgress: studySlice.actions.setProgress,
  setIRLevelMapping: studySlice.actions.setIRLevelMapping,
  setPaused: studySlice.actions.setPaused,
  setSynonyms: studySlice.actions.setSynonyms,
  loadSynonyms: createAction(`${studySlice.name}/loadSynonyms`, (payload) => ({
    payload,
  })),
  setSynonymsLoading: studySlice.actions.setSynonymsLoading,
  setRecapCompleted: studySlice.actions.setRecapCompleted,
  updateScores: studySlice.actions.updateScores,
  clearScores: studySlice.actions.clearScores,
  setAudioPlaying: studySlice.actions.setAudioPlaying,
  setFailed: studySlice.actions.setFailed,
  setFailedWords: studySlice.actions.setFailedWords,
  setSuccessWords: studySlice.actions.setSuccessWords,
  setActiveWords: studySlice.actions.setActiveWords,
  setActiveSentence: studySlice.actions.setActiveSentence,
  setFailedWordsForSentence: studySlice.actions.setFailedWordsForSentence,
  setSuccessWordsForSentence: studySlice.actions.setSuccessWordsForSentence,
  setMissedWordsForSentence: studySlice.actions.setMissedWordsForSentence,
  clearWordsHistory: studySlice.actions.clearWordsHistory,
};

export const getScores = (state) => state.study.scores;
export const getProgress = (state) => state.study.progress;
export const getIRLevelMapping = (state) => state.study.irLevelMapping;
export const getPaused = (state) => state.study.paused;
export const getSynonyms = (state) => state.study.synonyms;
export const synonymsLoading = (state) => state.study.synonymsLoading;
export const getRecapCompleted = (state) => state.study.recapCompleted;
export const audioPlaying = (state) => state.study.audioPlaying;
export const getFailed = (state) => state.study.failed;
export const getFailedWords = (state) => state.study.failedWords;
export const getSuccessWords = (state) => state.study.successWords;
export const getActiveWords = (state) => state.study.activeWords;
export const getActiveSentence = (state) => state.study.activeSentence;
export const getFailedWordsHistory = (state) => state.study.failedWordsHistory;
export const getSuccessWordsHistory = (state) =>
  state.study.successWordsHistory;
export const getMissedWordsHistory = (state) => state.study.missedWordsHistory;

export default studySlice.reducer;
